import React, { memo, useContext } from "react";
import { useTips } from "hooks/useTips";
import { formatPrice } from "helpers/formatNumber";
import { getSuggestedTipAmount } from "helpers/getSuggestedTipAmount";
import { Box, InputAdornment, TextField } from "@mui/material";
import {
  TipBlock,
  TipLabel,
  TipTitle,
  TipValue,
  TipWrapBlock,
  TPaymentTitle,
  TipValueOtherBox,
  CouponSubmitButton,
} from "components/styles";
import { TOContext } from "context/TContext";

const TOTips = ({ bId, oId }) => {
  const {
    tipStyleMemo,
    handleSelectTip,
    tipValue,
    selectedTip,
    loadingApplyTip,
    aggregatedTipOptions,
    handleChangeTip,
    handleClick,
    hasChange,
    hasUserInteraction,
    manualLoadingTip,
  } = useTips(bId, oId);

  const { charges, isRefundDueShow, actionInProgress } = useContext(TOContext);

  return (
    <>
      <TPaymentTitle>Payment Information</TPaymentTitle>
      <TipTitle sx={{ pb: 3 }}>
        How much staff tip do you want to leave?
      </TipTitle>
      <TipWrapBlock>
        {aggregatedTipOptions?.kitchenTip?.map((tip) => {
          const isSelected = selectedTip?.id === tip.id;
          const fontWeight = isSelected ? "bold" : "normal";
          const suggestedAmount = getSuggestedTipAmount(
            charges?.subTotal,
            tip,
            aggregatedTipOptions?.kitchenTip,
          );
          const v = Number(tip.value || 0);
          const calculated =
            v > 0 && v <= 1
              ? Number(charges?.subTotal || 0) * v
              : (Number(charges?.subTotal || 0) * v) / 100;
          const fallbackVal = tip.fallback ?? tip.fallbackAmount;
          // Guard v !== 0: a 0 % option should never show a fallback minimum
          const isFallbackApplied =
            v !== 0 && fallbackVal != null && calculated <= Number(fallbackVal);
          return (
            <React.Fragment key={tip.id}>
              <TipBlock
                disabled={
                  loadingApplyTip || isRefundDueShow || actionInProgress
                }
                loading={loadingApplyTip && isSelected}
                sx={{ border: tipStyleMemo(tip.id) }}
                onClick={() => handleSelectTip(tip)}
              >
                {/* Label is always visible so the user knows which option they're selecting */}
                <TipLabel sx={{ fontWeight: fontWeight }}>{tip.label}</TipLabel>
                <TipValue sx={{ fontWeight: fontWeight }}>
                  {formatPrice(suggestedAmount)}
                </TipValue>
                {/* (min) badge only when the fallback minimum overrides the calculated amount */}
                {/* {isFallbackApplied && (
                  <TipValue sx={{ fontWeight: fontWeight, fontSize: "0.7rem" }}>
                    (min)
                  </TipValue>
                )} */}
              </TipBlock>
            </React.Fragment>
          );
        })}
      </TipWrapBlock>
      <TipValueOtherBox>
        <Box sx={{ display: "flex", width: "100%" }}>
          <TextField
            value={tipValue}
            disabled={isRefundDueShow || actionInProgress}
            name="tip"
            label="Staff Tip *"
            inputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            inputProps={{
              min: 0.0,
              step: "any",
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRight: "none",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
              },
            }}
            onChange={handleChangeTip}
          />
          <CouponSubmitButton
            variant="contained"
            type={"submit"}
            loading={manualLoadingTip}
            disabled={
              (!hasChange && !hasUserInteraction) ||
              manualLoadingTip ||
              isRefundDueShow ||
              actionInProgress
            }
            onClick={handleClick}
          >
            ADD
          </CouponSubmitButton>
        </Box>
      </TipValueOtherBox>
    </>
  );
};

export default memo(TOTips);
